import React from "react";
import {
  ArrowLeft,
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  ClipboardList,
  Clock3,
  FileText,
  Headphones,
  MessageCircle,
  Star,
  UserRoundCheck,
  Users,
} from "lucide-react";
import { ambassadors } from "../../services/ambassadors";
import {
  AmbassadorContext,
  AmbassadorDashboard,
  ambassadorDate,
} from "../../types/ambassadors";

export default function AdminDashboard({
  dashboard,
  context,
  busy,
  canSupport,
  viewVersion,
  onOpenProfile,
  run,
}: {
  dashboard: AmbassadorDashboard;
  context: AmbassadorContext;
  busy: boolean;
  canSupport: boolean;
  viewVersion: number;
  onOpenProfile: (id: string) => void;
  run: (action: () => Promise<unknown>) => Promise<unknown>;
}) {
  const kpis = [
    {
      title: "کل پرونده‌ها",
      value: dashboard.total,
      caption: "پرونده‌های قابل دسترسی شما",
      Icon: Users,
      tone: "blue",
    },
    {
      title: "سفیر فعال",
      value: dashboard.statuses.ACTIVE || 0,
      caption: "همراهان در حال فعالیت",
      Icon: UserRoundCheck,
      tone: "green",
    },
    {
      title: "ورود ناقص",
      value: dashboard.incomplete.length,
      caption: "در صف تکمیل مراحل ورود",
      Icon: ClipboardList,
      tone: "amber",
    },
    {
      title: "درخواست باز",
      value: dashboard.openTickets,
      caption: "در انتظار رسیدگی پشتیبانی",
      Icon: Headphones,
      tone: "rose",
    },
  ];
  const rates = [
    { title: "نرخ پذیرش", value: dashboard.weekly.acceptanceRate },
    { title: "تکمیل ورود", value: dashboard.weekly.onboardingRate },
    { title: "استمرار فعالیت", value: dashboard.weekly.continuityRate },
    { title: "رسیدگی پشتیبانی", value: dashboard.weekly.supportRate },
  ];

  return (
    <div className="amb-dashboard">
      <div className="amb-section-title amb-view-heading">
        <div>
          <h2>نمای کلی تیم سفیران</h2>
          <p className="amb-muted">
            وضعیت امروز تیم و اولویت‌های پیگیری را یک‌جا ببینید.
          </p>
        </div>
        <span className="amb-period">
          <CalendarClock size={15} aria-hidden="true" /> به‌روزرسانی بر اساس
          آخرین اطلاعات
        </span>
      </div>
      <div className="amb-kpi-grid">
        {kpis.map(({ title, value, caption, Icon, tone }) => (
          <article className="amb-card amb-kpi" key={title}>
            <div className="amb-kpi-top">
              <span>{title}</span>
              <span className="amb-kpi-icon" data-tone={tone}>
                <Icon size={21} aria-hidden="true" />
              </span>
            </div>
            <strong className="amb-kpi-value">
              {Number(value || 0).toLocaleString("fa-IR")}
            </strong>
            <p>{caption}</p>
          </article>
        ))}
      </div>

      <section className="amb-card amb-weekly-panel">
        <div className="amb-section-title">
          <div className="amb-heading-with-icon">
            <span className="amb-heading-icon">
              <CheckCheck size={20} aria-hidden="true" />
            </span>
            <div>
              <h2>ارزیابی ۷ روز اخیر</h2>
              <p className="amb-muted">از پذیرش همکاری تا استمرار فعالیت</p>
            </div>
          </div>
          <span className="amb-period">بازه هفت‌روزه</span>
        </div>
        <div className="amb-rates-grid">
          {rates.map(({ title, value }) => (
            <div className="amb-rate" key={title}>
              <div>
                <span>{title}</span>
                <strong>
                  {value === null
                    ? "—"
                    : `${Number(value).toLocaleString("fa-IR")}٪`}
                </strong>
              </div>
              {value === null ? (
                <div className="amb-progress-empty" aria-hidden="true" />
              ) : (
                <progress
                  value={Math.max(0, Math.min(100, value))}
                  max={100}
                  aria-label={title}
                />
              )}
              <small>
                {value === null
                  ? "هنوز داده کافی ثبت نشده است"
                  : "بر پایه اطلاعات ثبت‌شده تیم"}
              </small>
            </div>
          ))}
        </div>
        <div className="amb-weekly-totals">
          <div>
            <span className="amb-metric-icon">
              <FileText size={18} aria-hidden="true" />
            </span>
            <span>
              <small>مطالب منتشرشده</small>
              <strong>
                {Number(dashboard.weekly.publishedCount || 0).toLocaleString(
                  "fa-IR",
                )}
              </strong>
            </span>
          </div>
          <div>
            <span className="amb-metric-icon">
              <MessageCircle size={18} aria-hidden="true" />
            </span>
            <span>
              <small>تعاملات معنادار</small>
              <strong>
                {Number(
                  dashboard.weekly.meaningfulInteractions || 0,
                ).toLocaleString("fa-IR")}
              </strong>
            </span>
          </div>
          <div>
            <span className="amb-metric-icon">
              <Star size={18} aria-hidden="true" />
            </span>
            <span>
              <small>کیفیت محتوا</small>
              <strong>
                {dashboard.weekly.averageQuality === null
                  ? "—"
                  : `${Number(dashboard.weekly.averageQuality).toLocaleString("fa-IR")} از ۵`}
              </strong>
            </span>
          </div>
        </div>
        <details className="amb-metric-definitions">
          <summary>مبنای محاسبه شاخص‌ها</summary>
          <p>{dashboard.definitions}</p>
          <p>
            کیفیت بر پایه{" "}
            {dashboard.weekly.reviewedReports.toLocaleString("fa-IR")} گزارش
            بررسی‌شده است. اهداف عددی پس از ۲ تا ۴ هفته آزمایشی تعیین می‌شوند.
          </p>
        </details>
      </section>

      <div className="amb-queue-grid">
        <section className="amb-card amb-queue">
          <div className="amb-section-title">
            <div className="amb-heading-with-icon">
              <span className="amb-heading-icon" data-tone="amber">
                <CalendarClock size={20} aria-hidden="true" />
              </span>
              <div>
                <h2>پیگیری‌های موعدرسیده</h2>
                <p className="amb-muted">اولویت تماس و هماهنگی با سفیران</p>
              </div>
            </div>
            <span className="amb-count">
              {dashboard.followUps.length.toLocaleString("fa-IR")}
            </span>
          </div>
          {dashboard.followUps.length ? (
            dashboard.followUps.map((profile) => (
              <button
                type="button"
                className="amb-queue-row"
                disabled={busy}
                key={profile.id}
                onClick={() => onOpenProfile(profile.id)}
              >
                <span className="amb-avatar" aria-hidden="true">
                  {Array.from(profile.fullName.trim())[0]}
                </span>
                <span>
                  <strong>{profile.fullName}</strong>
                  <small>
                    <Clock3 size={13} aria-hidden="true" />{" "}
                    {ambassadorDate(profile.nextFollowUpAt)}
                  </small>
                </span>
                <ArrowLeft size={16} aria-hidden="true" />
              </button>
            ))
          ) : (
            <div className="amb-empty amb-queue-empty">
              <CheckCircle2 size={30} aria-hidden="true" />
              <h3>پیگیری عقب‌افتاده‌ای ندارید.</h3>
              <p>موعدهای ثبت‌شده در پرونده‌ها، اینجا نمایش داده می‌شوند.</p>
            </div>
          )}
        </section>
        <section className="amb-card amb-queue">
          <div className="amb-section-title">
            <div className="amb-heading-with-icon">
              <span className="amb-heading-icon">
                <ClipboardList size={20} aria-hidden="true" />
              </span>
              <div>
                <h2>ورودهای ناقص</h2>
                <p className="amb-muted">همراهی تا تکمیل مراحل شروع همکاری</p>
              </div>
            </div>
            <span className="amb-count">
              {dashboard.incomplete.length.toLocaleString("fa-IR")}
            </span>
          </div>
          {dashboard.incomplete.length ? (
            dashboard.incomplete.map((profile) => (
              <button
                type="button"
                className="amb-queue-row"
                disabled={busy}
                key={profile.id}
                onClick={() => onOpenProfile(profile.id)}
              >
                <span className="amb-avatar" aria-hidden="true">
                  {Array.from(profile.fullName.trim())[0]}
                </span>
                <span>
                  <strong>{profile.fullName}</strong>
                  <small>بررسی چک‌لیست و قدم بعدی</small>
                </span>
                <ArrowLeft size={16} aria-hidden="true" />
              </button>
            ))
          ) : (
            <div className="amb-empty amb-queue-empty">
              <CheckCheck size={30} aria-hidden="true" />
              <h3>پرونده ناقصی وجود ندارد.</h3>
              <p>پرونده‌های نیازمند تکمیل مراحل ورود، اینجا قرار می‌گیرند.</p>
            </div>
          )}
        </section>
      </div>

      {canSupport && (
        <section className="amb-card amb-daily-panel">
          <div className="amb-section-title">
            <div className="amb-heading-with-icon">
              <span className="amb-heading-icon">
                <ClipboardList size={20} aria-hidden="true" />
              </span>
              <div>
                <h2>چک‌لیست روزانه پشتیبان</h2>
                <p className="amb-muted">
                  اقدام‌های امروز و نتیجه پیگیری‌ها را ثبت کنید.
                </p>
              </div>
            </div>
            <span className="amb-period">
              <Clock3 size={14} aria-hidden="true" /> روز کاری به وقت تهران
            </span>
          </div>
          <form
            key={`support:${viewVersion}`}
            onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              run(() =>
                ambassadors.supportDay(
                  data.getAll("items").map(String),
                  String(data.get("note") || ""),
                ),
              );
            }}
          >
            <fieldset disabled={busy}>
              <div className="amb-daily-layout">
                <div className="amb-daily-checks">
                  {context.supportDaily.map((title) => (
                    <label className="amb-check" key={title}>
                      <input
                        type="checkbox"
                        name="items"
                        value={title}
                        defaultChecked={dashboard.supportChecklist?.items.includes(
                          title,
                        )}
                      />
                      {title}
                    </label>
                  ))}
                </div>
                <label>
                  جمع‌بندی امروز
                  <textarea
                    name="note"
                    maxLength={4000}
                    defaultValue={dashboard.supportChecklist?.note}
                    placeholder="نتیجه تماس‌ها و اقدام‌های بعدی امروز…"
                  />
                </label>
              </div>
              <div className="amb-form-footer">
                <p className="amb-muted">
                  نتیجه پیگیری هر سفیر را در سابقه پرونده او هم ثبت کنید.
                </p>
                <button disabled={busy}>ذخیره چک‌لیست امروز</button>
              </div>
            </fieldset>
          </form>
        </section>
      )}
    </div>
  );
}
